import Task20 from '@carbon/icons-react/es/task/20';
import { Editor } from '@ckeditor/ckeditor5-core/src/editor';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  defaultDropAnimation,
  DropAnimation,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ProjectId } from 'api/models';
import {
  PublishedDataType,
  ResourceObject,
  StatusUpdateState,
} from 'api/status-updates';
import { humanise } from 'components/atoms/humanised-date';
import { Loading } from 'components/atoms/loading';
import { DocumentProvider } from 'components/organisms/document/document-store';
import { TaskDrawer } from 'components/organisms/task-drawer';
import { FilterableField } from 'components/organisms/tasks/fields';
import { CKEditorContext } from 'contexts';
import { TaskPaneContextProvider } from 'contexts/providers/task-pane';
import { store } from 'data';
import {
  selectActiveDocumentInfo,
  selectDocumentResourceSections,
  updateDocResource,
} from 'data/documents';
import { useProjectDetail } from 'data/projects';
import { selectActiveStatusUpdate } from 'data/status-updates';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import {
  PastProjectStatusField,
  LastMilestoneRatingField,
  CsatRatingField,
  ProjectHoursField,
  ProjectPhasesField,
} from 'routes/projects/list-v2/fields';
import { getHeaderName } from 'routes/projects/list-v2/grid/get-header-name';
import translations from 'translations/en.json';
import { projectFinancialFields } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { DraggableField, Overlay } from './draggable';
import { getSummaryFieldRenderer } from './get-cell-renderer';
import { ProjectFieldsFilterPopover } from './project-fields-filter';
import {
  Row,
  Col1,
  Col2,
  ProjectSummaryTitle,
  DragIcon,
  ProjectSummaryWrapper,
  DndWrapper,
} from './style';
import {
  MinimalNativeFieldType,
  getFieldNamesFromIds,
  getOrderedFields,
  getPayload,
  nativeFields,
} from './utils';

const defaultDropAnimationConfig: DropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.8,
};
const queryClient = new QueryClient();

export const ProjectSummaryBase = ({
  fieldIds,
  resourceSectionId,
  extraContextData,
  projectId: _projectId,
  statusUpdate,
}) => {
  const { data: timesheetConfiguration } = useTimesheetConfiguration();

  const { fields: allFields, loading: isFieldsLoading } =
    useProjectFieldsAsFilterableFields();
  const activeSU = useSelector(selectActiveStatusUpdate);

  const activeStatusUpdate = useMemo(
    () => statusUpdate ?? activeSU,
    [statusUpdate, activeSU],
  );

  const [publishedSummaryData, setPublishedSummaryData] =
    useState<PublishedDataType | null>(null);

  useEffect(() => {
    if (
      extraContextData &&
      extraContextData?.publishedData &&
      extraContextData?.publishedData?.length > 0 &&
      activeStatusUpdate?.state === StatusUpdateState.Published
    ) {
      let updatedData: PublishedDataType =
        (extraContextData &&
          extraContextData?.publishedData &&
          extraContextData?.publishedData?.find(
            (data) =>
              String(data?.resourceSectionId ?? -1) === resourceSectionId,
          )) ??
        null;

      const processArrays = (
        array1: ResourceObject[],
        array2: MinimalNativeFieldType[],
      ) => {
        const modifiedArray = array1?.map((item1) => {
          const commonItem = array2?.find(
            (item2) => item2.fieldName === item1.fieldName,
          );

          // If a matching item is found, update fieldType in array1
          if (commonItem) {
            return {
              ...item1,
              fieldType: commonItem?.type ?? '',
              fieldLabel: (
                <FormattedMessage id={commonItem?.fieldLabel ?? ''} />
              ) as any,
            };
          }

          // If no matching item is found, return the original item
          return item1;
        });

        return modifiedArray;
      };

      updatedData = {
        ...updatedData,
        resourceObjects: processArrays(
          updatedData?.resourceObjects ?? [],
          nativeFields,
        ),
      };

      setPublishedSummaryData(updatedData);
    }
  }, [resourceSectionId, extraContextData, activeStatusUpdate?.state]);

  const projectId = _projectId ?? activeStatusUpdate?.project.projectId;
  const isPublished = activeStatusUpdate?.state === StatusUpdateState.Published;
  const extraFields = useMemo(
    () => [
      PastProjectStatusField,
      LastMilestoneRatingField,
      CsatRatingField,
      ProjectHoursField,
      ProjectPhasesField,
    ],
    [],
  );
  const exCludedFields = useMemo(
    () => ['template', 'projectName'].concat(projectFinancialFields),
    [],
  );

  const allProjectFields = useMemo(
    () =>
      allFields
        ?.filter((field) => {
          const isFieldExcluded = exCludedFields.find(
            (excludedField) => excludedField === field.fieldName,
          );

          return !isFieldExcluded;
        })
        .concat(extraFields),
    [allFields, extraFields, exCludedFields],
  );

  const documentResources = useSelector(selectDocumentResourceSections);
  const activeDocumentDetails = useSelector(selectActiveDocumentInfo);
  const dispatch = useDispatch();
  const sectionResources =
    documentResources &&
    documentResources.find(
      (resource) => resource.resourceSectionId === Number(resourceSectionId),
    );

  const resourceNames =
    sectionResources && 'resourceNames' in sectionResources
      ? sectionResources.resourceNames
      : fieldIds;

  const [projectFields, setProjectFields] = useState<
    FilterableField[] | ResourceObject[]
  >(() => {
    return getOrderedFields(
      isPublished && publishedSummaryData
        ? publishedSummaryData?.resourceNames ?? []
        : resourceNames,
      isPublished && publishedSummaryData
        ? publishedSummaryData?.resourceObjects ?? []
        : allProjectFields,
      extraFields,
    );
  });

  const { data: projectDetail, isLoading: isProjectDetailLoading } =
    useProjectDetail(ProjectId(projectId ?? 0), {
      enabled: projectId !== null,
    });

  const [itemIds, setItemIds] = useState(() => {
    return projectFields?.map((field) => `${field.fieldId}`);
  });
  const itemIdsAsNumber = useMemo(
    () => itemIds?.map((id) => Number(id)),
    [itemIds],
  );

  useEffect(() => {
    if (!isFieldsLoading) {
      setItemIds(() => projectFields?.map((field) => `${field.fieldId}`));
    }
  }, [projectFields, sectionResources, isFieldsLoading]);

  useEffect(() => {
    if (!isFieldsLoading) {
      setProjectFields(() => {
        if (
          Array.isArray(
            isPublished && publishedSummaryData
              ? publishedSummaryData?.resourceNames
              : resourceNames,
          )
        ) {
          return getOrderedFields(
            isPublished && publishedSummaryData
              ? publishedSummaryData?.resourceNames ?? []
              : resourceNames,
            isPublished && publishedSummaryData
              ? publishedSummaryData?.resourceObjects ?? []
              : allProjectFields,
            extraFields,
          );
        }

        return [];
      });
    }
  }, [
    resourceNames,
    allProjectFields,
    fieldIds,
    extraFields,
    isFieldsLoading,
    publishedSummaryData,
    isPublished,
  ]);

  const [activeId, setActiveId] = useState(null);
  const [activeField, setActiveField] = useState<null | FilterableField>(null);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = itemIds.indexOf(active.id);
      const newIndex = itemIds.indexOf(over.id);
      const newOrder = arrayMove(itemIds, oldIndex, newIndex);

      setItemIds((items) => {
        const oldArrIndex = items.indexOf(active.id);
        const newArrIndex = items.indexOf(over.id);

        return arrayMove(items, oldArrIndex, newArrIndex);
      });
      const payload = getPayload(
        activeStatusUpdate,
        activeDocumentDetails,
        Number(resourceSectionId),
        documentResources,
        getFieldNamesFromIds(newOrder, allProjectFields),
      );

      if (payload) {
        setProjectFields(() => {
          return getOrderedFields(
            getFieldNamesFromIds(newOrder, allProjectFields),
            allProjectFields,
            extraFields,
          );
        });
        dispatch(updateDocResource(payload));
      }
    }
    setActiveId(null);
    setActiveField(null);
  };
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const handleDragStart = (event) => {
    const { active } = event;

    setActiveId(active.id);
    const currentField = allProjectFields.find(
      (field) => field.fieldId === Number(active.id),
    );

    setActiveField(currentField || null);
  };
  const handleFieldSelectChange = (fields: number[]) => {
    const payload = getPayload(
      activeStatusUpdate,
      activeDocumentDetails,
      Number(resourceSectionId),
      documentResources,
      getFieldNamesFromIds(fields, allProjectFields),
    );

    if (payload) {
      dispatch(updateDocResource(payload));
    }
  };
  const handleDragCancel = () => setActiveId(null);

  return (
    <>
      {!isProjectDetailLoading ? (
        <ProjectSummaryWrapper>
          <ProjectSummaryTitle>
            <Task20 />
            <div>
              <FormattedMessage
                id="project_summary.as.on.date"
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  date: humanise(
                    moment(activeStatusUpdate?.publishedAt ?? new Date()),
                  ),
                }}
              />
            </div>
          </ProjectSummaryTitle>

          <DndWrapper>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              sensors={sensors}
              onDragCancel={handleDragCancel}
              modifiers={[
                restrictToVerticalAxis,
                restrictToFirstScrollableAncestor,
              ]}
            >
              <SortableContext
                items={itemIds}
                strategy={verticalListSortingStrategy}
              >
                {!isFieldsLoading && projectDetail ? (
                  projectFields?.map((field) => {
                    return (
                      <DraggableField
                        key={field.fieldId}
                        id={field.fieldId}
                        isPublished={isPublished}
                      >
                        <Row isOverlay={false}>
                          <Col1>
                            {!isPublished ? (
                              <DragIcon className="dragIcon" />
                            ) : null}
                            <div>{getHeaderName(field)}</div>
                          </Col1>
                          <Col2>
                            {getSummaryFieldRenderer({
                              field,
                              projectDetails: projectDetail,
                              isPrivate: activeStatusUpdate?.private,
                              isPublished: Boolean(
                                isPublished && publishedSummaryData,
                              ),
                              publishedField:
                                isPublished && publishedSummaryData
                                  ? field
                                  : undefined,
                              timesheetConfiguration,
                            })}
                          </Col2>
                        </Row>
                      </DraggableField>
                    );
                  })
                ) : (
                  <Loading size="small" />
                )}
              </SortableContext>
              {createPortal(
                <DragOverlay
                  adjustScale={false}
                  dropAnimation={defaultDropAnimationConfig}
                  zIndex={1040}
                >
                  {activeField && activeId ? (
                    <Overlay field={activeField} />
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </DndContext>
          </DndWrapper>
          {!isPublished ? (
            <ProjectFieldsFilterPopover
              fieldIds={itemIdsAsNumber}
              fields={allProjectFields}
              handleFieldSelectChange={handleFieldSelectChange}
            />
          ) : null}
        </ProjectSummaryWrapper>
      ) : (
        <Loading size="large" />
      )}
    </>
  );
};
export const projectSummaryRenderer = (
  { fields, resourceSectionId },
  container: HTMLElement,
  editor: Editor,
  extraContextData,
  projectId,
  statusUpdate,
) => {
  const locale = 'en';

  ReactDOM.render(
    <Provider store={store}>
      <BrowserRouter>
        <TaskPaneContextProvider>
          <IntlProvider locale={locale} messages={translations}>
            <QueryClientProvider client={queryClient}>
              <TaskDrawer />
              <CKEditorContext.Provider value={editor}>
                <DocumentProvider>
                  <ProjectSummaryBase
                    statusUpdate={statusUpdate}
                    projectId={projectId}
                    fieldIds={fields}
                    resourceSectionId={resourceSectionId}
                    extraContextData={extraContextData}
                  />
                </DocumentProvider>
              </CKEditorContext.Provider>
            </QueryClientProvider>
          </IntlProvider>
        </TaskPaneContextProvider>
      </BrowserRouter>
    </Provider>,
    container,
  );
};
